/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC, ReactNode } from 'react';
import React, { useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import { FormattedMessage } from 'react-intl-next';

import type {
	Appearance,
	LoadOptions,
	OnChange,
	OnOption,
	Option,
	OptionData,
} from '@atlaskit/user-picker';
import UserPicker from '@atlaskit/user-picker';
import type { SelectComponentsConfig, StylesConfig } from '@atlaskit/select';
import SmartUserPicker, { type Props as SmartUserPickerProps } from '@atlaskit/smart-user-picker';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { getExternalCollaboratorEntitlementFromQuery } from '@confluence/external-collab-ui/entry-points/getExternalCollaboratorEntitlementFromQuery.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useLazyQueryPromiseInterop } from '@confluence/apollo-relay-interop';
import { expVal } from '@confluence/feature-experiments';

import type { UserAndGroupSearchPickerQuery as UserAndGroupSearchPickerQuery$data } from './__types__/UserAndGroupSearchPickerQuery.ts';
import { userGroupSearchPromise } from './UserAndGroupSearchPromise.ts';
import { UserAndGroupSearchQuery } from './UserAndGroupSearchQuery.graphql.ts';
import {
	SitePermissionTypeFilter,
	type UserAndGroupSearchQuery as UserAndGroupSearchQueryType,
	type UserAndGroupSearchQueryVariables,
} from './__types__/UserAndGroupSearchQuery.ts';

const MIN_SEARCH_LENGTH = 2;
export const DEBOUNCE_SEARCH_TIME_MS = 200;
export function isPermissionError(error) {
	if (
		('statusCode' in error && error['statusCode'] === 403) ||
		error.message?.includes('403') ||
		error.message?.includes('not allowed')
	) {
		return true;
	}

	return false;
}

const DefaultWrapper = ({ children }) => <Box>{children}</Box>;

export type WrapperProps = {
	children: ReactNode;
	'aria-label'?: string;
	'aria-describedby'?: string;
	role?: string;
};

export type UserGroupItemProps = OptionData & {
	// TODO: Update this type to be useful https://product-fabric.atlassian.net/browse/CPE-2272
	extra?: any;
};

export type CustomInclusion = {
	[matchedSearchTerm: string]: UserGroupItemProps;
}[];

export type UserGroupSingleMultiItem = UserGroupItemProps | UserGroupItemProps[];

export type UserAndGroupSearchPickerProps = {
	appearance?: Appearance;
	fieldId: string;
	isMulti: boolean;
	includeGroup: boolean;
	onSelect?: OnOption;
	onChange?: OnChange;
	onClear?: () => void;
	onFocus?: () => void;
	onBlur?: () => void;
	onOpen?: () => void;
	onClose?: () => void;
	onInputChange?: (query?: string, sessionId?: string) => void;
	onKeyDown?: (event: React.KeyboardEvent) => void;
	filterSearchResults?: (value: UserGroupItemProps) => boolean;
	smartFilterOptions?: (values: UserGroupItemProps[]) => UserGroupItemProps[];
	value?: UserGroupSingleMultiItem | null;
	defaultValue?: UserGroupSingleMultiItem;
	menuPortalTarget?: HTMLElement;
	noOptionsMessage:
		| ReactNode
		| ((value: { inputValue: string }) => string | null | React.ReactNode);
	addMoreMessage?: string;
	autoFocus?: boolean;
	width?: number | string;
	smart: boolean;
	placeholder?: string;
	// Allows filtering for certain user types (only when not using smart picker)
	// Use NONE to filter for only internal users,
	// EXTERNALCOLLABORATOR for only guest users,
	// ALL for both internal and guest users.
	// Smart picker filtering is based on the user's external collaboration entitlements,
	// and does not use this prop.
	sitePermissionTypeFilter?: SitePermissionTypeFilter;
	maxPickerHeight?: number;
	displayEmailInByline?: boolean;
	allowEmail?: boolean;
	suggestEmailsForDomain?: string;
	enableEmailSearch?: boolean;
	allowEmailSelectionWhenEmailMatched?: boolean;
	includeTeams?: boolean;
	includeTeamsUpdates?: boolean;
	/** When true, only verified teams are shown. Only applies when includeTeams is true. */
	verifiedTeams?: boolean;
	isClearable?: boolean;
	customInclusion?: CustomInclusion;
	transformOptions?: (options: OptionData[]) => Promise<OptionData[]>;
	menuPosition?: 'absolute' | 'fixed' | undefined;
	isDisabled?: boolean;
	isLoading?: boolean;
	styles?: StylesConfig;
	ariaLabel?: string;
	ariaDescribedBy?: string;
	role?: string;
	bootstrapOptions?: UserGroupItemProps[];
	inputId?: string;
	// TODO need to clean this up no longer in use
	withUsers?: boolean;
	wrapperWidth?: string;
	Wrapper?: FC<WrapperProps>;
	header?: React.ReactNode;
	open?: boolean;
	userpickerDescribedbyId?: string;
	includeUsers?: boolean;
	userResolvers?: SmartUserPickerProps['userResolvers'];
	components?: SelectComponentsConfig<Option, boolean>;
};

export const UserAndGroupSearchPickerComponent: FC<UserAndGroupSearchPickerProps> = ({
	appearance,
	fieldId,
	isMulti,
	includeGroup,
	onSelect,
	onChange,
	onClear,
	onFocus,
	onBlur,
	onOpen,
	onClose,
	onInputChange,
	onKeyDown,
	filterSearchResults,
	smartFilterOptions,
	value,
	defaultValue,
	noOptionsMessage,
	addMoreMessage,
	menuPortalTarget,
	autoFocus,
	width,
	maxPickerHeight,
	smart,
	styles,
	placeholder,
	sitePermissionTypeFilter = SitePermissionTypeFilter.NONE,
	displayEmailInByline,
	allowEmail,
	suggestEmailsForDomain,
	enableEmailSearch,
	allowEmailSelectionWhenEmailMatched,
	includeTeams,
	includeTeamsUpdates,
	verifiedTeams,
	menuPosition,
	customInclusion = [],
	ariaLabel,
	ariaDescribedBy,
	role,
	isDisabled = false,
	isLoading = false,
	bootstrapOptions = [],
	inputId,
	withUsers = true,
	Wrapper = DefaultWrapper,
	header,
	open,
	userpickerDescribedbyId,
	isClearable = true,
	includeUsers = true,
	transformOptions,
	userResolvers,
	components,
}) => {
	const [error, setError] = useState<Error | undefined>(undefined);
	const { cloudId, orgId } = useSessionData();
	const queryUserAndGroupSearch = useLazyQueryPromiseInterop<
		UserAndGroupSearchQueryType,
		UserAndGroupSearchQueryVariables
	>(
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		UserAndGroupSearchQuery,
	);

	const { loading: searchPickerLoading, data: searchPickerData } =
		useQuery<UserAndGroupSearchPickerQuery$data>(gql`
			query UserAndGroupSearchPickerQuery {
				entitlements {
					externalCollaborator {
						isEntitled
					}
				}
			}
		`);

	const handleError = (searchError) => {
		if (searchError) {
			setError(searchError);

			// mark error as handled so we don't have ui unhandled errors
			markErrorAsHandled(searchError);
		}
	};

	const promiseOptions: LoadOptions = (searchTerm) =>
		!searchTerm || searchTerm.length < MIN_SEARCH_LENGTH
			? []
			: userGroupSearchPromise({
					searchTerm,
					queryData: queryUserAndGroupSearch({
						variables: {
							withGroups: includeGroup,
							searchTerm,
							sitePermissionTypeFilter,
							withUsers,
						},
					}),
					customInclusion,
					setError,
					filterResults: filterSearchResults,
				});

	const searchErrorMessage = (
		<FormattedMessage
			id="user-and-group-search.search.error"
			defaultMessage="Sorry, we’re having trouble loading search results right now."
			description="Error message to explain search dysfunction"
		/>
	);

	const permissionErrorMessage = (
		<FormattedMessage
			id="user-and-group-search.permission.error"
			defaultMessage="We couldn’t find any results because you don't have permission to search the user directory."
			description="Error message when search fails due to the user not having permission"
		/>
	);

	// TODO: <UserPicker> expects `noOptionsMessage` to return string | null
	// but this callback returns JSX.Element | string | null, so error message must be string.
	const emptyResultsMessage = (input): any => {
		if (error) {
			if (isPermissionError(error)) {
				return permissionErrorMessage;
			}

			return searchErrorMessage;
		} else if (typeof noOptionsMessage === 'function') {
			return noOptionsMessage(input);
		}
		return noOptionsMessage;
	};

	const isEntitledExternalCollaboration =
		getExternalCollaboratorEntitlementFromQuery(searchPickerData) ?? false;

	const finalPlaceholder =
		placeholder ||
		(includeGroup ? (
			allowEmail ? (
				<FormattedMessage
					id="user-and-group-search.placeholder.text.users.groups.and.email"
					defaultMessage="Add people by name, email, or group"
					description="Default message visible on the user, email and group search input element, explaining what to do with it"
				/>
			) : (
				<FormattedMessage
					id="user-and-group-search.placeholder.text.users.and.groups"
					// TODO: a content designer should check this case. This isn't the usual sentence structure for placeholders.
					defaultMessage="Type a user name or group"
					description="Default message visible on the user and group search input element, explaining what to do with it"
				/>
			)
		) : allowEmail ? (
			<FormattedMessage
				id="user-and-group-search.placeholder.text.users.and.email"
				defaultMessage="Add people, emails"
				description="Default message visible on the user and email search input element, explaining what to do with it"
			/>
		) : (
			<FormattedMessage
				id="user-and-group-search.placeholder.text.users"
				// TODO: a content designer should check this case. This isn't the usual sentence structure for placeholders.
				defaultMessage="Type a user name"
				description="Default message visible on the user search input element, explaining what to do with it"
			/>
		));

	const commonProps = {
		appearance,
		placeholder: finalPlaceholder,
		addMoreMessage,
		isMulti,
		defaultValue,
		fieldId,
		menuPortalTarget,
		noOptionsMessage: emptyResultsMessage,
		onChange,
		onClear,
		onFocus,
		onOpen,
		onClose,
		onInputChange,
		onSelection: onSelect,
		value,
		width,
		maxPickerHeight,
		displayEmailInByline,
		allowEmail,
		suggestEmailsForDomain,
		enableEmailSearch,
		allowEmailSelectionWhenEmailMatched,
		styles,
		menuPosition,
		isDisabled,
		inputId,
		components: components as any, // Type assertion: runtime receives Option, but UserPicker types expect OptionData
		withUsers,
		header,
		open,
		'aria-labelledby': `react-select-${fieldId}-placeholder`,
		'aria-describedby': userpickerDescribedbyId,
		'aria-label': ariaLabel,
	};

	return (
		<Wrapper aria-label={ariaLabel} aria-describedby={ariaDescribedBy} role={role}>
			{error ? <ErrorDisplay error={error} /> : null}
			{!smart && (
				<UserPicker
					{...commonProps}
					inputId={inputId}
					autoFocus={autoFocus}
					loadOptions={promiseOptions}
					// bug with UserPicker forces us to use a callback rather than just pass in a string
					onBlur={onBlur}
					isLoading={isLoading}
					appearance={appearance}
					isClearable={isClearable}
				/>
			)}
			{smart && (
				<SmartUserPicker
					{...commonProps}
					inputId={inputId}
					debounceTime={DEBOUNCE_SEARCH_TIME_MS}
					includeGroups={includeGroup}
					includeUsers={includeUsers}
					isLoading={searchPickerLoading || isLoading}
					maxOptions={60}
					onError={handleError}
					filterOptions={smartFilterOptions}
					productAttributes={{
						isEntitledConfluenceExternalCollaborator: isEntitledExternalCollaboration,
					}}
					principalId="context" // infer principal ID from UCT
					productKey="confluence"
					autoFocus={autoFocus}
					siteId={cloudId}
					orgId={orgId}
					includeTeams={includeTeams}
					includeTeamsUpdates={includeTeamsUpdates}
					verifiedTeams={verifiedTeams}
					bootstrapOptions={bootstrapOptions}
					onBlur={onBlur}
					onKeyDown={onKeyDown}
					isClearable={isClearable}
					transformOptions={transformOptions}
					userResolvers={userResolvers}
					displayEmailInByline={expVal(
						'confluence-enable-email-ux-in-user-group-search',
						'isEnabled',
						false,
					)}
					enableEmailSearch={expVal(
						'confluence-enable-email-ux-in-user-group-search',
						'isEnabled',
						false,
					)}
					allowEmailSelectionWhenEmailMatched={
						!expVal('confluence-enable-email-ux-in-user-group-search', 'isEnabled', false)
					}
				/>
			)}
		</Wrapper>
	);
};

/**
 * ⚠️  DEPRECATED PACKAGE WARNING ⚠️
 *
 * @deprecated This package (@confluence/user-and-group-search) is deprecated.
 *
 * Please use the official Atlaskit components instead:
 * - For smart user picker: '@atlaskit/smart-user-picker'
 * - For standard user picker: '@atlaskit/user-picker'
 *
 * These provide ongoing support.
 *
 */
export const UserAndGroupSearchPicker = UserAndGroupSearchPickerComponent;
